// BrokerWorkboard — replaces the simple BrokerCalendar.
// Two views toggled by a pill: List (urgency-ranked) and Week (calendar grid).
// Every client card has a quick-action strip: Call · Email · Message · Open · + Appt
// Email triggers an AI-drafted message the broker can edit before sending via mailto.

import { useState } from "react";
import { STAGES, STAGE_COUNT } from "../seller/stages";
import { callAI } from "../seller/lib/api";
import { loadAppts, saveAppts, addAppt, delAppt, APPT_TYPES, toDateStr, weekDays } from "../lib/appointments";

const mono  = "'DM Mono',monospace";
const serif = "'Playfair Display',serif";
const sans  = "'Outfit','Inter',sans-serif";

const fmtPrice = (n) => n == null ? "" : `$${Math.round(n).toLocaleString()}`;
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TYPE_COLORS = {
  call: "#3b6ea8", showing: "#7a5ea8", open_house: "#c47c1a",
  photographer: "#2d7a62", staging: "#a85e2d", closing: "#c0354a", other: "#5a8a78",
};

// ─── Appointment modal ────────────────────────────────────────────────────────
function ApptModal({ clients, prefillClient, prefillDate, onSave, onClose }) {
  const [form, setForm] = useState({
    client_id:   prefillClient?.id || "",
    client_name: prefillClient?.name || "",
    title: "",
    type:  "call",
    date:  prefillDate || toDateStr(new Date()),
    time:  "10:00",
    notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.date) return;
    onSave(form);
    onClose();
  };

  const inp = { background: "#f7faf8", border: "1.5px solid #d8ede6", borderRadius: 8, padding: "9px 12px", fontSize: 13, fontFamily: sans, color: "#1c2b25", width: "100%", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,25,20,0.55)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 20, padding: 28, width: "100%", maxWidth: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.18)" }}>
        <div style={{ fontFamily: serif, fontSize: 20, fontWeight: 700, color: "#1c2b25", marginBottom: 20 }}>Add appointment</div>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {!prefillClient && (
            <select value={form.client_id} onChange={(e) => {
              const c = clients.find((x) => x.id === e.target.value);
              set("client_id", e.target.value); set("client_name", c?.name || "");
            }} style={inp}>
              <option value="">Select client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Title — e.g. Open house at 123 Main" style={inp} autoFocus required />
          <select value={form.type} onChange={(e) => set("type", e.target.value)} style={inp}>
            {APPT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} style={inp} required />
            <input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} style={inp} />
          </div>
          <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notes (optional)" rows={2} style={{ ...inp, resize: "none" }} />
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button type="submit" style={{ flex: 1, background: "#2d7a62", color: "#fff", border: "none", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: sans }}>Save →</button>
            <button type="button" onClick={onClose} style={{ background: "#f2f8f5", color: "#5a8a78", border: "1px solid #d8ede6", borderRadius: 10, padding: "13px 16px", fontSize: 13, cursor: "pointer", fontFamily: mono }}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── AI email draft modal ─────────────────────────────────────────────────────
function EmailModal({ client, onClose }) {
  const [draft, setDraft]   = useState("");
  const [subject, setSubject] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied]  = useState(false);

  const currentStageLabel = (() => {
    if (client.type !== "seller") return null;
    const idx = client.currentStageIdx || 0;
    return STAGES[idx]?.short || null;
  })();

  const context = client.type === "seller"
    ? `They are selling their property${client.address ? ` at ${client.address}` : ""}. Current stage: ${currentStageLabel || "getting started"}.`
    : `They are a buyer looking in ${client.location || "their area"}${client.price_max ? `, budget up to ${fmtPrice(client.price_max)}` : ""}.`;

  useState(() => {
    const generate = async () => {
      try {
        const { text } = await callAI({
          system: "You are a professional real estate broker. Write brief, warm, professional client emails. Under 120 words. No subject line. No sign-off.",
          prompt: `Write a follow-up email to ${client.name}.\n${context}\nBe warm, direct, and include one clear next step.`,
          maxTokens: 250,
        });
        setDraft(text.trim());
        setSubject(`Following up — ${currentStageLabel || (client.type === "seller" ? "your listing" : "your home search")}`);
      } catch {
        setDraft(`Hi ${client.name},\n\nJust wanted to check in and see how things are going. Let me know if you have any questions or need anything from me.\n\nLooking forward to connecting soon.`);
        setSubject("Checking in");
      }
      setLoading(false);
    };
    generate();
  });

  const openMail = () => {
    const url = `mailto:${client.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(draft)}`;
    window.open(url, "_blank");
  };

  const copy = () => {
    navigator.clipboard.writeText(draft).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,25,20,0.55)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 20, padding: 28, width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontFamily: serif, fontSize: 18, fontWeight: 700, color: "#1c2b25" }}>Email {client.name}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#9bbfb2", fontSize: 20 }}>×</button>
        </div>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" style={{ width: "100%", background: "#f7faf8", border: "1.5px solid #d8ede6", borderRadius: 8, padding: "9px 12px", fontSize: 13, fontFamily: sans, color: "#1c2b25", marginBottom: 10, boxSizing: "border-box" }} />
        {loading ? (
          <div style={{ textAlign: "center", padding: "24px 0", color: "#9bbfb2", fontFamily: mono, fontSize: 12 }}>Drafting email…</div>
        ) : (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={7}
            style={{ width: "100%", background: "#f7faf8", border: "1.5px solid #d8ede6", borderRadius: 8, padding: "10px 12px", fontSize: 13, fontFamily: sans, color: "#1c2b25", resize: "vertical", boxSizing: "border-box" }}
          />
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button onClick={openMail} disabled={loading || !draft} style={{ flex: 1, background: "#2d7a62", color: "#fff", border: "none", borderRadius: 10, padding: 12, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: sans }}>Open in mail →</button>
          <button onClick={copy} disabled={loading || !draft} style={{ background: "#f2f8f5", color: "#1c2b25", border: "1px solid #d8ede6", borderRadius: 10, padding: "12px 16px", fontSize: 13, cursor: "pointer", fontFamily: sans }}>
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Client action strip ──────────────────────────────────────────────────────
function ActionStrip({ client, onMessage, onOpen, onEmail, onAddAppt }) {
  const btn = (label, onClick, color = "#f2f8f5", textColor = "#1c2b25", href = null) => {
    const style = { background: color, color: textColor, border: `1px solid ${color === "#f2f8f5" ? "#d8ede6" : "transparent"}`, borderRadius: 6, padding: "4px 10px", fontSize: 11, fontFamily: mono, cursor: "pointer", letterSpacing: "0.04em", textDecoration: "none", display: "inline-block" };
    return href
      ? <a key={label} href={href} style={style}>{label}</a>
      : <button key={label} type="button" onClick={onClick} style={style}>{label}</button>;
  };

  return (
    <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
      {client.phone && btn("📞 Call", null, "#f2f8f5", "#1c2b25", `tel:${client.phone}`)}
      {client.email && btn("✉ Email", onEmail)}
      {btn("💬 Message", onMessage, "#e4f7ef", "#2d7a62")}
      {btn("→ Open", onOpen)}
      {btn("+ Appt", onAddAppt, "#fff", "#2d7a62")}
    </div>
  );
}

// ─── List view ────────────────────────────────────────────────────────────────
function ListView({ clients, appts, weekRef, onSelectClient, onEmail, onAddAppt }) {
  const now = Date.now();

  const buildItem = (c) => {
    const hoursAgo = c.updated_at ? (now - new Date(c.updated_at).getTime()) / 3600000 : 999;
    if (c.type === "seller") {
      const done = STAGES.filter((s) => c.stages?.[s.id]?.status === "done").length;
      const cur  = STAGES[c.currentStageIdx || 0];
      const st   = c.stages?.[cur?.id]?.status || "pending";
      const stuck = hoursAgo > 48 && st !== "done";
      return { ...c, hoursAgo, stuck, curStage: cur?.short || "—", stageStatus: st, done };
    }
    return { ...c, hoursAgo, stuck: false };
  };

  const items = clients.map(buildItem).sort((a, b) => (b.stuck ? 1 : -1) || a.hoursAgo - b.hoursAgo);
  const fmt   = (h) => h >= 999 ? "new" : h < 1 ? "just now" : h < 24 ? `${Math.round(h)}h ago` : `${Math.round(h / 24)}d ago`;

  const Card = ({ item }) => (
    <div style={{ background: item.stuck ? "#fff8f8" : "#f7fdf9", border: `1.5px solid ${item.stuck ? "#f2c4cb" : "#d8ede6"}`, borderRadius: 14, padding: "14px 18px", marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#1c2b25", fontFamily: sans }}>{item.name}</span>
          <span style={{ fontSize: 9, fontFamily: mono, background: item.type === "seller" ? "#e4f7ef" : "#e4eef7", color: item.type === "seller" ? "#2d7a62" : "#3b6ea8", padding: "2px 7px", borderRadius: 6, letterSpacing: "0.06em" }}>{item.type.toUpperCase()}</span>
        </div>
        <span style={{ fontSize: 10, fontFamily: mono, color: item.stuck ? "#c0354a" : "#9bbfb2", flexShrink: 0, marginLeft: 8 }}>{fmt(item.hoursAgo)}</span>
      </div>
      {item.type === "seller" && item.address && (
        <div style={{ fontSize: 11, color: "#7aad98", fontFamily: mono, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.address}</div>
      )}
      {item.type === "seller" && (
        <div style={{ fontSize: 12, color: "#5a8a78", marginTop: 5, fontFamily: sans }}>
          Step: <strong>{item.curStage}</strong>
          <span style={{ marginLeft: 10, fontFamily: mono, fontSize: 10, color: item.stageStatus === "done" ? "#2d7a62" : item.stageStatus === "running" ? "#c47c1a" : "#9bbfb2" }}>{item.stageStatus}</span>
          <span style={{ marginLeft: 10, fontFamily: mono, fontSize: 10, color: "#9bbfb2" }}>{item.done}/{STAGE_COUNT} done</span>
        </div>
      )}
      {item.type === "buyer" && (
        <div style={{ fontSize: 12, color: "#5a8a78", marginTop: 5, fontFamily: sans }}>
          {[item.location, item.price_max ? `up to ${fmtPrice(item.price_max)}` : null].filter(Boolean).join(" · ")}
        </div>
      )}
      {item.stuck && (
        <div style={{ fontSize: 11, color: "#c0354a", fontFamily: mono, marginTop: 6 }}>⚠ No activity in {Math.round(item.hoursAgo / 24)}d</div>
      )}
      <ActionStrip
        client={item}
        onMessage={() => onSelectClient(item.id, "messages")}
        onOpen={() => onSelectClient(item.id, "journey")}
        onEmail={() => onEmail(item)}
        onAddAppt={() => onAddAppt(item)}
      />
    </div>
  );

  const stuck  = items.filter((i) => i.stuck);
  const active = items.filter((i) => !i.stuck);

  const upcomingAppts = appts
    .filter((a) => a.date >= toDateStr(new Date()))
    .sort((a, b) => a.date.localeCompare(b.date) || (a.time || "").localeCompare(b.time || ""))
    .slice(0, 5);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px", display: "flex", gap: 28 }}>
      {/* Clients */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {stuck.length > 0 && (
          <>
            <div style={{ fontFamily: mono, fontSize: 10, color: "#c0354a", letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>Needs attention</div>
            {stuck.map((i) => <Card key={i.id} item={i} />)}
            <div style={{ marginBottom: 20 }} />
          </>
        )}
        {active.length > 0 && (
          <>
            <div style={{ fontFamily: mono, fontSize: 10, color: "#2d7a62", letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>Active</div>
            {active.map((i) => <Card key={i.id} item={i} />)}
          </>
        )}
        {clients.length === 0 && (
          <div style={{ textAlign: "center", color: "#9bbfb2", padding: "60px 20px" }}>
            <div style={{ fontFamily: serif, fontSize: 18, marginBottom: 8, color: "#5a8a78" }}>No clients yet</div>
            <p style={{ fontSize: 13, lineHeight: 1.65 }}>Add a seller or buyer client to see their agenda here.</p>
          </div>
        )}
      </div>

      {/* Upcoming appointments sidebar */}
      {upcomingAppts.length > 0 && (
        <div style={{ width: 220, flexShrink: 0 }}>
          <div style={{ fontFamily: mono, fontSize: 10, color: "#5a8a78", letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>Upcoming</div>
          {upcomingAppts.map((a) => {
            const d = new Date(a.date + "T12:00:00");
            const isToday = toDateStr(new Date()) === a.date;
            return (
              <div key={a.id} style={{ background: "#fff", border: "1.5px solid #d8ede6", borderLeft: `3px solid ${TYPE_COLORS[a.type] || "#5a8a78"}`, borderRadius: 10, padding: "10px 12px", marginBottom: 8 }}>
                <div style={{ fontSize: 10, fontFamily: mono, color: TYPE_COLORS[a.type] || "#5a8a78", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 2 }}>{a.type.replace("_", " ")}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1c2b25", fontFamily: sans, marginBottom: 3 }}>{a.title}</div>
                <div style={{ fontSize: 11, fontFamily: mono, color: "#9bbfb2" }}>
                  {isToday ? "Today" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  {a.time ? ` · ${a.time.slice(0, 5).replace(/^0/, "").replace(":00", "")}${a.time < "12:00" ? "am" : "pm"}` : ""}
                </div>
                {a.client_name && <div style={{ fontSize: 10, fontFamily: mono, color: "#9bbfb2", marginTop: 2 }}>{a.client_name}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Week calendar view ───────────────────────────────────────────────────────
function WeekView({ clients, appts, days, onAddAppt, onSelectClient, onDeleteAppt }) {
  const todayStr = toDateStr(new Date());

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "0 28px 28px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 10, paddingTop: 20 }}>
        {days.map((day, di) => {
          const ds     = toDateStr(day);
          const isToday = ds === todayStr;
          const dayAppts = appts.filter((a) => a.date === ds).sort((a, b) => (a.time || "").localeCompare(b.time || ""));

          return (
            <div key={ds} style={{ minHeight: 180 }}>
              {/* Day header */}
              <div style={{ marginBottom: 8, textAlign: "center" }}>
                <div style={{ fontFamily: mono, fontSize: 10, color: "#9bbfb2", letterSpacing: "0.1em", textTransform: "uppercase" }}>{DAY_LABELS[di]}</div>
                <div style={{ fontFamily: sans, fontSize: 16, fontWeight: 700, color: isToday ? "#2d7a62" : "#1c2b25", background: isToday ? "#e4f7ef" : "transparent", borderRadius: 8, padding: "2px 0", marginTop: 2 }}>
                  {day.getDate()}
                </div>
              </div>

              {/* Appointments */}
              {dayAppts.map((a) => (
                <div key={a.id} style={{ background: "#fff", border: `1.5px solid ${TYPE_COLORS[a.type] || "#d8ede6"}`, borderLeft: `3px solid ${TYPE_COLORS[a.type] || "#9bbfb2"}`, borderRadius: 8, padding: "6px 8px", marginBottom: 6, cursor: "pointer" }}
                  title={a.notes || a.title}
                  onClick={() => {
                    const c = clients.find((x) => x.id === a.client_id);
                    if (c) onSelectClient(c.id, "journey");
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#1c2b25", fontFamily: sans, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.title}</div>
                  {a.time && <div style={{ fontSize: 10, fontFamily: mono, color: "#9bbfb2" }}>{a.time.slice(0, 5)}</div>}
                  {a.client_name && <div style={{ fontSize: 10, fontFamily: mono, color: TYPE_COLORS[a.type] || "#9bbfb2" }}>{a.client_name}</div>}
                  <button type="button" onClick={(e) => { e.stopPropagation(); onDeleteAppt(a.id); }}
                    style={{ background: "none", border: "none", color: "#c0354a", fontSize: 10, cursor: "pointer", fontFamily: mono, padding: "2px 0 0", letterSpacing: "0.04em" }}>
                    remove
                  </button>
                </div>
              ))}

              {/* Add slot */}
              <button type="button" onClick={() => onAddAppt(null, ds)}
                style={{ width: "100%", background: "none", border: "1px dashed #d8ede6", borderRadius: 8, padding: "6px", fontSize: 10, fontFamily: mono, color: "#9bbfb2", cursor: "pointer", letterSpacing: "0.06em" }}>
                + add
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main workboard ───────────────────────────────────────────────────────────
export default function BrokerWorkboard({ clients, onSelectClient }) {
  const [calView, setCalView]       = useState("list");      // "list" | "week"
  const [weekOffset, setWeekOffset] = useState(0);
  const [appts, setAppts]           = useState(loadAppts);
  const [apptModal, setApptModal]   = useState(null);        // { client?, date? } | null
  const [emailModal, setEmailModal] = useState(null);        // client | null

  const days = weekDays((() => { const d = new Date(); d.setDate(d.getDate() + weekOffset * 7); return d; })());

  const handleAddAppt = (client, date) => setApptModal({ client: client || null, date: date || null });

  const handleSaveAppt = (form) => {
    const c = clients.find((x) => x.id === form.client_id);
    setAppts(addAppt({ ...form, client_name: c?.name || form.client_name || "" }));
  };

  const handleDeleteAppt = (id) => setAppts(delAppt(id));

  const handleSelectClient = (id, tab) => onSelectClient(id, tab);

  const weekLabel = weekOffset === 0 ? "This week"
    : weekOffset === 1 ? "Next week"
    : weekOffset === -1 ? "Last week"
    : `${days[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${days[6].toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
      {/* Modals */}
      {apptModal && (
        <ApptModal
          clients={clients}
          prefillClient={apptModal.client}
          prefillDate={apptModal.date}
          onSave={handleSaveAppt}
          onClose={() => setApptModal(null)}
        />
      )}
      {emailModal && (
        <EmailModal client={emailModal} onClose={() => setEmailModal(null)} />
      )}

      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 28px", borderBottom: "1px solid #f0f4f2", flexShrink: 0, background: "#fff" }}>
        <div style={{ fontFamily: serif, fontSize: 18, fontWeight: 700, color: "#1c2b25" }}>
          {calView === "week" ? weekLabel : "Workboard"}
        </div>
        {calView === "week" && (
          <div style={{ display: "flex", gap: 4 }}>
            <button onClick={() => setWeekOffset((o) => o - 1)} style={{ background: "#f2f8f5", border: "1px solid #d8ede6", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontFamily: mono, fontSize: 12, color: "#5a8a78" }}>←</button>
            <button onClick={() => setWeekOffset(0)} style={{ background: weekOffset === 0 ? "#2d7a62" : "#f2f8f5", color: weekOffset === 0 ? "#fff" : "#5a8a78", border: "1px solid #d8ede6", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontFamily: mono, fontSize: 11 }}>today</button>
            <button onClick={() => setWeekOffset((o) => o + 1)} style={{ background: "#f2f8f5", border: "1px solid #d8ede6", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontFamily: mono, fontSize: 12, color: "#5a8a78" }}>→</button>
          </div>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={() => handleAddAppt(null, null)} style={{ background: "#f2f8f5", border: "1px solid #d8ede6", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontFamily: mono, color: "#2d7a62", cursor: "pointer", letterSpacing: "0.04em" }}>
          + Appointment
        </button>
        {/* List / Week toggle */}
        <div style={{ display: "flex", background: "#f2f8f5", borderRadius: 8, padding: 3, border: "1px solid #d8ede6" }}>
          {[["list", "List"], ["week", "Week"]].map(([v, label]) => (
            <button key={v} onClick={() => setCalView(v)} style={{
              background: calView === v ? "#fff" : "none", border: "none", borderRadius: 6,
              padding: "4px 12px", fontSize: 11, fontFamily: mono, letterSpacing: "0.06em",
              color: calView === v ? "#1c2b25" : "#9bbfb2", cursor: "pointer",
              fontWeight: calView === v ? 700 : 400, boxShadow: calView === v ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
              transition: "all 0.1s",
            }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {calView === "list" ? (
        <ListView
          clients={clients}
          appts={appts}
          onSelectClient={handleSelectClient}
          onEmail={(c) => setEmailModal(c)}
          onAddAppt={(c) => handleAddAppt(c, null)}
        />
      ) : (
        <WeekView
          clients={clients}
          appts={appts}
          days={days}
          onAddAppt={handleAddAppt}
          onSelectClient={handleSelectClient}
          onDeleteAppt={handleDeleteAppt}
        />
      )}
    </div>
  );
}
